import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Dropdown, SearchInput, Text } from '@common-fe/common-fe';
import _ from 'lodash';

import { OrganizationTypes } from '@/common/constants';
import { useCurrentOrganization } from '@/modules/core';

import { useEmployersDropdownListQuery } from './queries';
interface Props {
  isAutoFocus?: boolean;
  onSetAutoFocus?: (value: boolean) => void;
  value: string;
  onChangeValue: (value: string) => void;
  testId?: string;
  disabled?: boolean;
}
const MIN_CHARS = 3;
const EmployerSelector: React.FC<Props> = ({
  testId = 'employer-selector',
  isAutoFocus,
  value,
  onChangeValue,
  onSetAutoFocus,
  disabled,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { observingOrganization } = useCurrentOrganization();
  const isSubsidiary = useMemo(() => {
    return observingOrganization?.type === OrganizationTypes.subsidiary;
  }, [observingOrganization]);

  const [searchString, setSearchString] = useState('');

  const { options, isLoading, handleLoadMore, defaultEmployer } = useEmployersDropdownListQuery({
    searchString: _.trim(searchString || '').length >= MIN_CHARS ? searchString : '',
    defaultEmployerId: value,
  });

  const handlePickItem = useCallback(
    (key: string, title: string) => () => {
      onChangeValue(key);
      setSearchString(title);
      setExpanded(false);
    },
    [onChangeValue]
  );
  useEffect(() => {
    if (defaultEmployer && !searchString && value) {
      setSearchString(defaultEmployer.title || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultEmployer, value]);
  const handleChangeSearchString = useCallback(
    (newValue: string) => {
      if (!disabled) {
        setSearchString(newValue);
        onChangeValue('');
      }
    },
    [disabled, onChangeValue]
  );
  const nodes = useMemo(() => {
    return options.map((item) => (
      <Dropdown.Item
        testId={`${testId}_item_${item.key}`}
        key={item.key}
        onClick={handlePickItem(item.key as string, item.title || '')}
      >
        <Box overflow="hidden">
          <Text truncate title={item.title}>
            {item.title}
          </Text>
        </Box>
      </Dropdown.Item>
    ));
  }, [handlePickItem, options, testId]);

  const handleToggleExpand = useCallback(
    (visible: boolean) => {
      if (!disabled) {
        setExpanded(visible);
        if (!visible && !value) {
          setSearchString('');
        }
      }
    },
    [disabled, value]
  );

  const handleAutoFocusBlur = useCallback(() => {
    if (onSetAutoFocus) {
      onSetAutoFocus(false);
    }
  }, [onSetAutoFocus]);

  return (
    <Dropdown
      placeholder="Placeholder"
      expanded={expanded}
      disabled={isSubsidiary}
      testId={`${testId}_dropdown`}
      isFlatMode
      title={
        <SearchInput
          key={`${isAutoFocus}`}
          disabled={isSubsidiary || disabled}
          autoFocus={isAutoFocus}
          testId={`${testId}_search`}
          onFocus={() => {
            setExpanded(true);
          }}
          onBlur={handleAutoFocusBlur}
          placeholder="Select employer"
          value={searchString}
          onChange={disabled ? () => {} : handleChangeSearchString}
        />
      }
      onSetExpanded={handleToggleExpand}
    >
      {nodes.length || isLoading ? (
        <Box pad="spacing8">
          <Dropdown.InfiniteLoading
            testId={`${testId}_loading`}
            onLoadMore={handleLoadMore}
            isLoadMoreAvailable
            isLoading={isLoading}
            itemHeight={36}
            nodes={nodes}
            wrapperBox={{
              height: {
                max: '250px',
              },
            }}
          />
        </Box>
      ) : (
        <Box pad="spacing12">
          <Text data-testid={`${testId}_not-found`} color="textSecondary">
            Not found
          </Text>
        </Box>
      )}
    </Dropdown>
  );
};
export default EmployerSelector;
